/*
Purpose: Shows recent jobs/analyses for a specific evidence item.
Audience: Users reviewing past analyses to open details.
What it does:
- Lists job entries with provider/model/status
- Provides a "View Detailed Results" action that opens OrderResultsModal
Interplay:
- Reads Firestore jobs; navigates to OrderResultsModal with focusJobId
*/
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { FaSearch, FaFilter, FaSort, FaClock, FaUser, FaRobot, FaCheckCircle, FaExclamationCircle, FaSpinner, FaEye, FaChevronDown, FaChevronUp, FaTimes } from 'react-icons/fa';
import { SingleOrderResultsModal } from '../modals/results/wrappers/CleanResultsModals';
import '../../styles/AnalysisLog.css';

const AnalysisLog = ({ evidenceId, casefileId, currentUser, onAnalysisSelect, selectedAnalysisId }) => {
  const [analyses, setAnalyses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedAnalysis, setExpandedAnalysis] = useState(null);
  const [userDisplayNames, setUserDisplayNames] = useState({});
  const [modalAnalysis, setModalAnalysis] = useState(null);
  const [openOrderId, setOpenOrderId] = useState(null);
  const [focusJobId, setFocusJobId] = useState(null);
  const [casefileName, setCasefileName] = useState(null);
  const [evidenceName, setEvidenceName] = useState(null);

  // Fetch casefile and evidence names for breadcrumb context
  useEffect(() => {
    const fetchNames = async () => {
      try {
        if (casefileId) {
          const casefileDoc = await getDoc(doc(db, 'casefiles', casefileId));
          if (casefileDoc.exists()) {
            const casefileData = casefileDoc.data();
            setCasefileName(casefileData.name || casefileData.title || 'Untitled Casefile');
          }
        }
        
        if (evidenceId) {
          const evidenceDoc = await getDoc(doc(db, 'evidence', evidenceId));
          if (evidenceDoc.exists()) {
            const evidenceData = evidenceDoc.data();
            setEvidenceName(evidenceData.fileName || evidenceData.name || 'Untitled Evidence');
          }
        }
      } catch (error) {
        console.error('Error fetching names:', error);
      }
    };
    
    if (casefileId || evidenceId) {
      fetchNames();
    }
  }, [casefileId, evidenceId]);

  // Fetch analysis history from unified jobs collection
  useEffect(() => {
    if (!evidenceId || !casefileId) return;

    // Use unified jobs collection from Order → Jobs architecture
    const jobsRef = collection(db, 'jobs');
    const q = query(
      jobsRef,
      where('evidenceId', '==', evidenceId),
      where('casefileId', '==', casefileId)
    );

    const unsubscribe = onSnapshot(q, async (querySnapshot) => {
      const analysisData = [];
      const userIds = new Set();

      // Process unified jobs for this specific evidence
      querySnapshot.forEach((doc) => {
        const job = { id: doc.id, ...doc.data() };
        
        // Map unified job schema to expected UI schema
        const uiAnalysis = {
          id: doc.id,
          analysisJobId: job.jobId || doc.id,
          jobStatus: job.status,
          createdAt: job.createdAt,
          completedAt: job.completedAt,
          processingDuration: job.processingDuration,
          initiatedByUid: job.userId,
          actualCost: job.actualCost,
          confidenceScore: job.confidenceScore,
          selectedServices: job.selectedServices,
          orderId: job.orderId, // Include order reference
          provider: job.provider, // Include provider info
          selectedModel: job.selectedModel, // Include model info
          analysisText: job.analysisText || job.analysis, // Include analysis text
          ...job // Include all other fields
        };
        
        analysisData.push(uiAnalysis);
        if (job.userId) {
          userIds.add(job.userId);
        }
      });

      // Sort client-side by createdAt (newest first) to replace server-side orderBy
      analysisData.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || a.createdAt || new Date(0);
        const bTime = b.createdAt?.toDate?.() || b.createdAt || new Date(0);
        return bTime - aTime; // Descending order (newest first)
      });

      setAnalyses(analysisData);
      
      // Auto-select the most recent completed analysis if none is selected
      if (analysisData.length > 0 && !selectedAnalysisId && onAnalysisSelect) {
        const mostRecentCompleted = analysisData.find(a => a.jobStatus === 'completed');
        if (mostRecentCompleted) {
          onAnalysisSelect(mostRecentCompleted);
        } else {
          // If no completed analysis, select the most recent one
          onAnalysisSelect(analysisData[0]);
        }
      }
      
      // Fetch user display names
      if (userIds.size > 0) {
        await fetchUserDisplayNames(Array.from(userIds));
      }
      
      setIsLoading(false);
    }, (error) => {
      console.error("Error fetching analysis history:", error);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [evidenceId, casefileId, selectedAnalysisId, onAnalysisSelect]);

  // Fetch user display names
  const fetchUserDisplayNames = async (userIds) => {
    try {
      const names = {};
      
      for (const userId of userIds) {
        try {
          const userDocRef = doc(db, 'userProfiles', userId);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists() && userDoc.data().displayName) {
            names[userId] = userDoc.data().displayName;
          } else {
            // Fallback to a portion of the user ID
            names[userId] = `User ${userId.substring(0, 8)}...`;
          }
        } catch (err) {
          console.error(`Error fetching user ${userId}:`, err);
          names[userId] = 'Unknown User';
        }
      }
      
      setUserDisplayNames(names);
    } catch (error) {
      console.error("Error fetching user display names:", error);
    }
  };

  // Filter and sort analyses
  const filteredAndSortedAnalyses = useMemo(() => {
    let filtered = analyses;

    // Apply search filter
    if (searchTerm.trim()) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(analysis => {
        const summary = analysis.summary?.toLowerCase() || '';
        const analysisType = analysis.analysisType?.toLowerCase() || '';
        const userName = userDisplayNames[analysis.initiatedByUid]?.toLowerCase() || '';
        const analysisText = analysis.analysis?.toLowerCase() || '';
        
        return summary.includes(lowerSearchTerm) ||
               analysisType.includes(lowerSearchTerm) ||
               userName.includes(lowerSearchTerm) ||
               analysisText.includes(lowerSearchTerm);
      });
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(analysis => analysis.jobStatus === statusFilter);
    }

    // Apply user filter
    if (userFilter !== 'all') {
      if (userFilter === 'me' && currentUser) {
        filtered = filtered.filter(analysis => analysis.initiatedByUid === currentUser.uid);
      } else if (userFilter !== 'me') {
        filtered = filtered.filter(analysis => analysis.initiatedByUid === userFilter);
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return (b.createdAt?.toDate() || new Date(0)) - (a.createdAt?.toDate() || new Date(0));
        case 'oldest':
          return (a.createdAt?.toDate() || new Date(0)) - (b.createdAt?.toDate() || new Date(0));
        case 'status':
          const statusOrder = { 'processing': 0, 'completed': 1, 'failed': 2 };
          return (statusOrder[a.jobStatus] || 3) - (statusOrder[b.jobStatus] || 3);
        case 'confidence':
          return (b.confidenceScore || 0) - (a.confidenceScore || 0);
        case 'duration':
          return (b.processingDuration || 0) - (a.processingDuration || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [analyses, searchTerm, statusFilter, userFilter, sortBy, userDisplayNames, currentUser]);

  // Get unique users for filter dropdown
  const uniqueUsers = useMemo(() => {
    const users = new Set();
    analyses.forEach(analysis => {
      if (analysis.initiatedByUid) {
        users.add(analysis.initiatedByUid);
      }
    });
    return Array.from(users);
  }, [analyses]);

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInMs = now - date;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  // Format processing duration
  const formatDuration = (durationMs) => {
    if (!durationMs) return 'N/A';
    const seconds = Math.round(durationMs / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Get status icon and color
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'processing':
        return { icon: FaSpinner, color: '#ffc107', text: 'Processing', spin: true };
      case 'completed':
        return { icon: FaCheckCircle, color: '#28a745', text: 'Completed', spin: false };
      case 'failed':
        return { icon: FaExclamationCircle, color: '#dc3545', text: 'Failed', spin: false };
      default:
        return { icon: FaClock, color: '#6c757d', text: 'Unknown', spin: false };
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setUserFilter('all');
    setSortBy('newest');
  };

  // Handle analysis selection and expansion
  const handleAnalysisSelect = (analysis) => {
    // Select the analysis for parent component
    if (onAnalysisSelect) {
      onAnalysisSelect(analysis);
    }
    // Also expand the analysis details in this component
    toggleAnalysisExpansion(analysis.id);
  };

  // Toggle analysis expansion
  const toggleAnalysisExpansion = (analysisId) => {
    setExpandedAnalysis(expandedAnalysis === analysisId ? null : analysisId);
  };

  if (isLoading) {
    return (
      <div className="analysis-log-loading">
        <FaSpinner className="loading-spinner" />
        <p>Loading analysis history...</p>
      </div>
    );
  }

  return (
    <div className="analysis-log">
      <div className="analysis-log-header">
        <h3>
          <FaRobot className="header-icon" />
          Analysis History
          <span className="analysis-count">({analyses.length})</span>
        </h3>
        
        <button 
          className={`filter-toggle ${showFilters ? 'active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <FaFilter />
          Filters
          {showFilters ? <FaChevronUp /> : <FaChevronDown />}
        </button>
      </div>

      {/* Filter Controls */}
      {showFilters && (
        <div className="analysis-filters">
          <div className="filter-row">
            <div className="filter-group">
              <label>
                <FaSearch />
                Search
              </label>
              <input
                type="text"
                placeholder="Search analyses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="filter-group">
              <label>Status</label>
              <select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Status</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            <div className="filter-group">
              <label>User</label>
              <select 
                value={userFilter} 
                onChange={(e) => setUserFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Users</option>
                {currentUser && <option value="me">My Analyses</option>}
                {uniqueUsers.map(userId => (
                  <option key={userId} value={userId}>
                    {userDisplayNames[userId] || 'Unknown User'}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>
                <FaSort />
                Sort By
              </label>
              <select 
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)}
                className="filter-select"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="status">By Status</option>
                <option value="confidence">By Confidence</option>
                <option value="duration">By Duration</option>
              </select>
            </div>
          </div>

          <div className="filter-actions">
            <button onClick={clearFilters} className="clear-filters-btn">
              <FaTimes />
              Clear Filters
            </button>
            <span className="results-count">
              Showing {filteredAndSortedAnalyses.length} of {analyses.length} analyses
            </span>
          </div>
        </div>
      )}

      {/* Analysis List */}
      <div className="analysis-list">
        {filteredAndSortedAnalyses.length === 0 ? (
          <div className="no-analyses">
            {analyses.length === 0 ? (
              <p>No analyses have been performed on this evidence yet.</p>
            ) : (
              <p>No analyses match your current filters.</p>
            )}
          </div>
        ) : (
          filteredAndSortedAnalyses.map((analysis) => {
            const statusDisplay = getStatusDisplay(analysis.jobStatus);
            const StatusIcon = statusDisplay.icon;
            const isExpanded = expandedAnalysis === analysis.id;
            const userName = userDisplayNames[analysis.initiatedByUid] || 'Unknown User';
            const isCurrentUser = currentUser && analysis.initiatedByUid === currentUser.uid;

            const isSelected = selectedAnalysisId === analysis.id;

            return (
              <div key={analysis.id} className={`analysis-item ${analysis.jobStatus} ${isSelected ? 'selected' : ''}`}>
                <div className="analysis-header" onClick={() => handleAnalysisSelect(analysis)}>
                  <div className="analysis-selection-indicator">
                    {isSelected && <div className="selected-dot"></div>}
                  </div>
                  <div className="analysis-status">
                    <StatusIcon 
                      className={`status-icon ${statusDisplay.spin ? 'spinning' : ''}`}
                      style={{ color: statusDisplay.color }}
                    />
                    <span className="status-text" style={{ color: statusDisplay.color }}>
                      {statusDisplay.text}
                    </span>
                  </div>

                  <div className="analysis-info">
                    <div className="analysis-summary">
                      {analysis.analysisText || analysis.analysis || analysis.summary || 
                       (analysis.jobStatus === 'completed' ? 'Analysis completed successfully' : 'Analysis in progress...')}
                    </div>
                    <div className="analysis-meta">
                      <span className="analysis-user">
                        <FaUser />
                        {isCurrentUser ? 'You' : userName}
                      </span>
                      <span className="analysis-time">
                        <FaClock />
                        {formatTimeAgo(analysis.createdAt)}
                      </span>
                      {analysis.confidence && (
                        <span className="analysis-confidence">
                          Confidence: {Math.round(analysis.confidence * 100)}%
                        </span>
                      )}
                      {analysis.processingDuration && (
                        <span className="analysis-duration">
                          Duration: {formatDuration(analysis.processingDuration)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="analysis-expand" onClick={(e) => {
                    e.stopPropagation();
                    toggleAnalysisExpansion(analysis.id);
                  }}>
                    <FaEye />
                    {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="analysis-details">
                    {/* Technical Specifications - Verification Sheet */}
                    <div className="analysis-spec-sheet" style={{
                      backgroundColor: '#2a2a2a',
                      border: '1px solid #4CAF50',
                      borderRadius: '8px',
                      padding: '16px',
                      marginBottom: '16px'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '12px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid #444'
                      }}>
                        <FaCheckCircle style={{ color: '#4CAF50', fontSize: '1.1em' }} />
                        <strong style={{ color: '#4CAF50', fontSize: '1em' }}>
                          Verified AI Analysis Specifications
                        </strong>
                      </div>
                      
                      <div className="spec-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '12px',
                        fontSize: '0.9em'
                      }}>
                        <div className="spec-item">
                          <div style={{ color: '#888', fontSize: '0.8em', marginBottom: '2px' }}>AI Model:</div>
                          <div style={{ color: '#fff', fontWeight: '500' }}>
                            {analysis.selectedModel || analysis.aiModel || 'Gemini 2.0 Flash'}
                          </div>
                        </div>
                        
                        {(analysis.processingDuration || analysis.processingTime) && (
                          <div className="spec-item">
                            <div style={{ color: '#888', fontSize: '0.8em', marginBottom: '2px' }}>Processing Time:</div>
                            <div style={{ color: '#fff', fontWeight: '500' }}>
                              {analysis.processingDuration ? 
                                `${Math.round(analysis.processingDuration / 1000)}s` : 
                                `${analysis.processingTime}s`
                              }
                            </div>
                          </div>
                        )}
                        
                        <div className="spec-item">
                          <div style={{ color: '#888', fontSize: '0.8em', marginBottom: '2px' }}>Authentication:</div>
                          <div style={{ 
                            color: '#4CAF50', 
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <FaCheckCircle style={{ fontSize: '0.9em' }} />
                            Verified API Response
                          </div>
                        </div>
                        
                        <div className="spec-item">
                          <div style={{ color: '#888', fontSize: '0.8em', marginBottom: '2px' }}>Analysis Status:</div>
                          <div style={{ 
                            color: analysis.jobStatus === 'completed' ? '#4CAF50' : 
                                   analysis.jobStatus === 'processing' ? '#ffc107' : 
                                   analysis.jobStatus === 'failed' ? '#dc3545' : '#888',
                            fontWeight: '500'
                          }}>
                            {analysis.jobStatus === 'completed' ? 'Complete' : 
                             analysis.jobStatus === 'processing' ? 'Processing' : 
                             analysis.jobStatus === 'failed' ? 'Failed' : 
                             analysis.status || 'Unknown'}
                          </div>
                        </div>
                        
                        {(analysis.costEstimation?.actualCost || analysis.actualCost) && (
                          <div className="spec-item">
                            <div style={{ color: '#888', fontSize: '0.8em', marginBottom: '2px' }}>Analysis Cost:</div>
                            <div style={{ color: '#fff', fontWeight: '500' }}>
                              ${(analysis.costEstimation?.actualCost || analysis.actualCost).toFixed(4)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="details-grid">
                      <div className="detail-item">
                        <strong>Analysis Targets:</strong>
                        <span>
                          {analysis.analysisRequest?.targets ? 
                            analysis.analysisRequest.targets.join(', ').replace(/_/g, ' ') : 
                            analysis.selectedServices ? 
                            analysis.selectedServices.join(', ').replace(/_/g, ' ') :
                            'Standard Detection'
                          }
                        </span>
                      </div>
                      {analysis.provider && (
                        <div className="detail-item">
                          <strong>AI Provider:</strong>
                          <span>
                            {analysis.provider === 'vertex_ai' ? 'Google Vertex AI' : 
                             analysis.provider === 'openai' ? 'OpenAI' :
                             analysis.provider === 'aws_bedrock' ? 'AWS Bedrock' :
                             analysis.provider}
                          </span>
                        </div>
                      )}
                      {analysis.completedAt && (
                        <div className="detail-item">
                          <strong>Completed:</strong>
                          <span>{new Date(analysis.completedAt.toDate()).toLocaleString()}</span>
                        </div>
                      )}
                    </div>

                    {/* Objects Detected */}
                    {analysis.objectsDetected && analysis.objectsDetected.length > 0 && (
                      <div className="objects-detected">
                        <strong>Objects Detected:</strong>
                        <ul>
                          {analysis.objectsDetected.map((obj, index) => (
                            <li key={index}>
                              {obj.type} (Confidence: {Math.round(obj.confidence * 100)}%)
                              {obj.description && ` - ${obj.description}`}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* View Detailed Results Button */}
                    {(analysis.analysisText || analysis.analysis) && (
                      <div style={{ marginTop: '15px', textAlign: 'center' }}>
                        <button 
                          className="view-detailed-results"
                          onClick={() => {
                            if (analysis.orderId) {
                              setOpenOrderId(analysis.orderId);
                              setFocusJobId(analysis.id);
                            } else {
                              // For jobs without orderId, show results inline
                              setModalAnalysis(analysis);
                            }
                          }}
                          style={{
                            background: '#6366f1',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '10px 20px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            margin: '0 auto',
                            transition: 'background 0.2s ease'
                          }}
                          onMouseOver={(e) => e.target.style.background = '#5856eb'}
                          onMouseOut={(e) => e.target.style.background = '#6366f1'}
                        >
                          <FaEye />
                          View Detailed Results
                        </button>
                      </div>
                    )}

                    {/* Error Message */}
                    {analysis.jobStatus === 'failed' && analysis.errorMessage && (
                      <div className="error-message">
                        <strong>Error:</strong>
                        <span>{analysis.errorMessage}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Preferred: Order Results Modal (single-order view) */}
      {openOrderId && (
        <SingleOrderResultsModal
          isOpen={!!openOrderId}
          onClose={() => { setOpenOrderId(null); setFocusJobId(null); }}
          orderId={openOrderId}
          focusJobId={focusJobId}
          origin="evidence"
          casefileId={casefileId}
          casefileName={casefileName}
          evidenceId={evidenceId}
          evidenceName={evidenceName}
          currentUser={currentUser}
        />
      )}

      {/* Fallback for jobs without orderId */}
      {modalAnalysis && (
        <div className="modal-overlay" onClick={() => setModalAnalysis(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{
            background: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '8px',
            padding: '20px',
            maxWidth: '800px',
            maxHeight: '80vh',
            overflow: 'auto',
            color: '#fff'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>Analysis Results</h3>
              <button onClick={() => setModalAnalysis(null)} style={{
                background: 'none',
                border: 'none',
                color: '#fff',
                fontSize: '20px',
                cursor: 'pointer'
              }}>×</button>
            </div>
            
            <div style={{ marginBottom: '15px' }}>
              <strong>Analysis Summary:</strong>
              <div style={{
                background: '#2a2a2a',
                padding: '15px',
                borderRadius: '5px',
                marginTop: '8px',
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                fontSize: '14px'
              }}>
                {modalAnalysis.analysisText || modalAnalysis.analysis || 'No summary available'}
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', fontSize: '14px' }}>
              <div>
                <strong>Provider:</strong><br />
                {modalAnalysis.provider || 'Unknown'}
              </div>
              <div>
                <strong>Model:</strong><br />
                {modalAnalysis.selectedModel || 'Unknown'}
              </div>
              <div>
                <strong>Status:</strong><br />
                {modalAnalysis.jobStatus || modalAnalysis.status || 'Unknown'}
              </div>
              <div>
                <strong>Duration:</strong><br />
                {modalAnalysis.processingDuration ? `${Math.round(modalAnalysis.processingDuration / 1000)}s` : 'Unknown'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysisLog; 